import React, { useContext, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import ReactMarkdown from 'react-markdown';
import { Resizable } from 're-resizable';
import axios from 'axios';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, CloseButton, HStack, Text, Textarea, useToast } from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { colors } from '../../App/ChakraTheme';
import { Tabs } from '../../components/Tabs';
import { AuthContext, Role } from '../../context/AuthProvider';
import { getData } from '../../hooks/useFetchEndPoint';
import styled from '../../consts/styled';
import Loader from '../../components/Loader';
import { Module } from '../app/pages/Class/Analytics/components/Module';
import { StyledTable } from '../app/pages/Class/Analytics/components/StyledTable';
import Drawer from '../../components/Drawer';
import CloudinaryUploadWidget from '../app/components/CloudinaryUploadWidget';
import { trackAction } from '../../helpers/trackAction';
import useWindowSize from '../app/hooks/useWindowSize';
import { ChatIcon, QuestionIcon } from '../../components/icons';
import SendFeedback from './components/SendFeedback/SendFeedback';
import './components/AskQuestion.css';

const LOCAL_STORAGE_KEY = 'chat-width';
const OLD_ANSWER_COUNT_KEY = 'old-answer-count';
const MIN_WIDTH = 280;

const Container = styled.div`
  position: fixed;
  z-index: 1001;
  transition: 1s;
  top: 0;
  bottom: 0;
  width: fit-content;
  box-shadow: 3px 3px 5px 0px #ccc;
`;

export const ChatButton = styled.button`
  z-index: 1001;
  position: fixed;
  width: 48px;
  height: 48px;
  left: calc(100% - 84px);
  bottom: 32px;
  padding: 10px;
  border-radius: 100px;
  border: none;
  background: #2833aa;
  box-shadow: 0px 0px 16px 0px rgba(0, 0, 0, 0.12);
  transition: 0.3s;
  &:before {
    opacity: 0;
    right: 64px;
    content: attr(data-hover);
    position: absolute;
    width: max-content;
    text-align: right;
    bottom: 12px;
    transform: translate(-100%, 0);
    transition: all 0.3s ease-in-out;
    font-weight: 500;
    font-size: 14px;
    pointer-events: none;
  }

  &:hover {
    box-shadow: 0px 0px 20px 4px rgba(0, 0, 0, 0.24);
    cursor: pointer;
    &:before {
      opacity: 1;
      transform: translate(0, 0);
    }
  }
`;

export const UnreadChannels = styled.span`
  position: absolute;
  top: -5px;
  left: 30px;
  background: red;
  border-radius: 100px;
  width: 20px;
  height: 20px;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;
  font-weight: bold;
  font-height: 10px;
  line-height: 20px;
`;

const UnreadAnswers = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  background: #605e5e;
  border-radius: 100px;
  width: 20px;
  height: 20px;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;
  font-weight: bold;
  font-height: 10px;
  line-height: 20px;
`;

const MarkdownDisplay = styled(ReactMarkdown)`
  img {
    max-width: 100px;
    display: block;
  }
`;



interface ChatProps {
  setIsTeamChatVisible: (value: boolean) => void;
}

const Chat = (props: ChatProps) => {
  const { setIsTeamChatVisible } = props;
  const user = useContext(AuthContext);
  const [chatData, setChatData] = useState<any>(null);
  const [_showChat, setShowChat] = useState(false);
  const [initialShowChat, setInitialShowChat] = useState(false);
  const chatDataUsername = chatData?.platform_user?.email;
  const chatDataPassword = chatData?.platform_user?.password;
  const chatDataDisplayName = chatData?.platform_user?.username;
  const [unreadChannels, setUnreadChannels] = useState(0);
  const [studentQuestion, setStudentQuestion] = useState('');
  const [showAskQuestionContainer, setShowAskQuestionContainer] =
    useState(false);
  const [isAskingQuestion, setIsAskingQuestion] = useState(false);
  const [askedQuestionsData, setAskedQuestionsData] = useState<any>({});
  const [tabActiveIndex, setTabActiveIndex] = useState(0);
  const [openedQuestions, setOpenedQuestions] = useState<number[]>([]);
  const [answerCount, setAnswerCount] = useState(0);
  const [isOpenAnswerCount, setIsOpenAnswerCount] = useState(true);
  const [imageInfo, setImageInfo] = useState<any>(null);
  const { width: WINDOW_WIDTH } = useWindowSize();

  const isStudent =
    user?.roles?.includes(Role.student) && user?.roles.length === 1;
  const isChatAvailable = user && user.is_authenticated && !user.pending;
  const isSchoolAllowingStudentChannels =
    chatData?.permissions?.allowDeviceChannels ||
    chatData?.permissions?.allowSchoolChannels;
  
  const { t } = useTranslation();
  const toast= useToast();

  useEffect(() => {
    if (isChatAvailable) {
      getData('/api/v1/clients/chat/', {}, (data) => {
        setChatData(data);
        setTimeout(() => {
          setInitialShowChat(true);
          setIsTeamChatVisible(true);
        }, 2000);
        if (data && isStudent) {
          getAskedQuestions();
          setInterval(() => {
            getAskedQuestions();
          }, 15000);
        }
      });
    }
  }, [user]);

  const handleUnreadChannels = (e: MessageEvent<any>) => {
    if (e.origin.includes('chat')) {
      setUnreadChannels(e.data.unreadChannels);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    addEventListener('message', (e) => handleUnreadChannels(e), true);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (showAskQuestionContainer) {
        setShowAskQuestionContainer(false);
        document.getElementById('question-btn')?.focus();
      } else if (_showChat) {
        setShowChat(false);
        document.getElementById('chat-btn')?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [_showChat, showAskQuestionContainer]);

  useEffect(() => {
    if (askedQuestionsData) {
      setIsOpenAnswerCount(true);
      const newAnswerCount = askedQuestionsData?.posts?.length
        ? askedQuestionsData.posts.reduce((acc: number, post: any) => {
            return acc + (Object.values(post.threads?.posts).length || 0);
          }, 0)
        : 0;
      setAnswerCount(newAnswerCount);
      const oldAnswerCount = localStorage.getItem(OLD_ANSWER_COUNT_KEY);
      if (oldAnswerCount) {
        try {
          setIsOpenAnswerCount(parseInt(oldAnswerCount) === newAnswerCount);
        } catch (e) {}
      }
    }
  }, [askedQuestionsData]);

  const [width, setWidth] = useState(
    parseInt(localStorage.getItem(LOCAL_STORAGE_KEY) || '768'),
  );

  useEffect(() => {
    if (WINDOW_WIDTH < 400) {
      return;
    }
    if (width > WINDOW_WIDTH - 200) {
      setChatWidth(WINDOW_WIDTH - 200);
    }
  }, [WINDOW_WIDTH]);

  const setChatWidth = (newWidth: number) => {
    setWidth(newWidth);
    localStorage.setItem(LOCAL_STORAGE_KEY, newWidth.toString());
  };

  const getAskedQuestions = () => {
    getData('/api/v1/clients/chat/ask-question/', {}, (data) => {
      if (data && data.posts) {
        data.posts = data.posts.map((post: any) => {
          const newPosts: any = {};
          Object.keys(post.threads.posts).forEach((key) => {
            if (post.threads.posts[key].message.includes('[ANSWER]')) {
              newPosts[key] = post.threads.posts[key];
            }
          });
          post.threads.posts = newPosts;
          return post;
        });
      }
      if (!data) {
        data = {};
      }
      if (!data.posts) {
        data.posts = [];
      }
      if (!data.order) {
        data.order = [];
      }
      setAskedQuestionsData(data);
    });
  };

  const askQuestion = async (question: string) => {
    if (imageInfo) {
      question += `\n![image question](${imageInfo.url})`;
    }
    setIsAskingQuestion(true);
    try {
      await axios.post(
        '/api/v1/clients/chat/ask-question/',
        {
          question,
        },
        {
          headers: {
            'X-CSRFToken': JSON.parse(
              document.getElementById('csrf_token')!.innerHTML,
            ),
          },
        },
      );
    } catch (e) {
      console.log(e);
    }
    setIsAskingQuestion(false);
    setShowAskQuestionContainer(false);
    getAskedQuestions();
  };

  const toggleQuestion = (index: number) => {
    if (openedQuestions.includes(index)) {
      openedQuestions.splice(openedQuestions.indexOf(index), 1);
    } else {
      openedQuestions.push(index);
    }
    setOpenedQuestions([...openedQuestions]);
  };

  const openChat = () => {
    if (isMobile) {
      trackAction({ action_type: 'chat_open', data: { mobile: true } });
      if (user && user.chat_host) {
        window.open(user.chat_host, '_blank');
      }
      setShowChat(false);
    } else {
      if (showChat) {
        trackAction({ action_type: 'chat_close' });
      } else {
        trackAction({ action_type: 'chat_open' });
      }
      setShowChat(!showChat);
    }
  };

  if (!user || isStudent === undefined) {
    return null;
  }

  if (!isChatAvailable) {
    return null;
  }
  if (!chatDataUsername || !chatDataPassword) {
    return null;
  }

  const isRoleHidingChat = !isSchoolAllowingStudentChannels && isStudent;
  const showChat = isRoleHidingChat ? false : _showChat;

  return (
    <>
      <Container
        id="chat-container"
        style={{
          display: initialShowChat ? 'block' : 'none',
          left: showChat ? 0 : -1 * (width + 50),
          opacity: showChat ? 1 : 0,
        }}
      >
        <Resizable
          onResize={(
            event: any,
            direction: any,
            elementRef: HTMLElement,
            delta: any,
          ) => {
            setChatWidth(parseInt(elementRef.style.width));
          }}
          minWidth={MIN_WIDTH}
          maxWidth={WINDOW_WIDTH - 200}
          defaultSize={{
            width,
            height: '100%',
          }}
          enable={{ right: true }}
          handleWrapperStyle={{ width: 0 }}
          handleStyles={{ right: { width: 0 } }}
          handleComponent={{
            right: (
              <div
                style={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  display: 'flex',
                  width: 50,
                }}
              >
                <div
                  style={{
                    boxShadow: ' 3px 3px 5px 0px #ccc',
                    padding: 10,
                    borderTopRightRadius: '12px',
                    borderBottomRightRadius: '12px',
                    width: '10px',
                    height: '50px',
                    background: '#FFF',
                    marginLeft: '-40px',
                  }}
                />
              </div>
            ),
          }}
        >
          {!isRoleHidingChat && (
            <iframe
              referrerPolicy="unsafe-url"
              title="chat"
              src={`${user?.chat_host}?username=${encodeURIComponent(
                chatDataUsername,
              )}&password=${encodeURIComponent(
                chatDataPassword,
              )}&display_name=${chatDataDisplayName}`}
              width={'100%'}
              style={{
                height: '100vh',
                border: 'none',
              }}
            ></iframe>
          )}
        </Resizable>
      </Container>
      {showAskQuestionContainer && (
        <Drawer>
          <CloseButton
            style={{ position: 'absolute', right: '24px', top: '24px' }}
            onClick={() => setShowAskQuestionContainer(false)}
          />
          <div style={{ maxWidth: '300px' }}>
            <Tabs
              style={{ justifyContent: 'center' }}
              activeIndex={tabActiveIndex}
              tabNames={[t("chat.oldQuestions"), t("chat.askAQuestion"), t("chat.sendFeedback")]}
              onClick={(index: number) => setTabActiveIndex(index)}
            />
          </div>
          {tabActiveIndex === 0 ? (
            <div>
              {askedQuestionsData?.posts?.length === 0 ? (
                <div>
                  <Text
                    className="body-x-small"
                    textStyle="bodyXSmall"
                    style={{ marginBottom: '20px' }}
                  >
                    {t("chat.noQuestionsFound")}
                  </Text>
                  <Button
                    colorScheme="blue"
                    onClick={() => setTabActiveIndex(1)}
                  >
                    {t("chat.askAQuestion")}
                  </Button>
                </div>
              ) : null}
              {askedQuestionsData?.order?.map((questionId: any, i: number) => {
                const askedQuestion = askedQuestionsData.posts.find(
                  (p: any) => p.id === questionId,
                );
                if (!askedQuestion) {
                  return null;
                }
                const activeQuestion = openedQuestions.includes(i);
                const delimiter1 = '**\n>';
                const delimiter2 = 'Komentirajte ovo';
                const question1 = askedQuestion.message.includes(delimiter1)
                  ? askedQuestion.message.split(delimiter1)[1]
                  : askedQuestion.message;
                const question = question1.includes(delimiter2)
                  ? question1.split(delimiter2)[0]
                  : question1;
                const answersCount =
                  Object.keys(askedQuestion.threads?.posts).length || 0;
                return (
                  <Module className={'module'} id={`module-${i}`} key={i} style={{
                    borderBottom: '0.5px solid gray',
                    marginBottom: '25px'
                  }}>
                    <div
                      style={{
                        alignItems: 'flex-start',
                        width: '100%',
                      }}
                      data-number={i + 1}
                      className={classNames([
                        'module-title',
                        { 'active-module': activeQuestion },
                      ])}
                      onClick={() => toggleQuestion(i)}
                    >
                      <HStack className="col-cs">
                        <UnreadAnswers
                          style={{
                            left: '-5px',
                            top: '-5px',
                          }}
                        >
                          {answersCount}
                        </UnreadAnswers>
                        <Text as="h5" textStyle="h5">
                          <MarkdownDisplay>{question.replace('Reply to send an answer', '')}</MarkdownDisplay>
                        </Text>
                        <ChevronRightIcon boxSize="24px" />
                      </HStack>
                    </div>
                    {activeQuestion ? (
                      <StyledTable>
                        {askedQuestion.threads?.order?.map(
                          (postId: any, j: number) => {
                            if (postId === askedQuestion.id) {
                              return null;
                            }
                            const post = askedQuestion.threads.posts[postId];
                            if (!post) {
                              return null;
                            }
                            const message = post.message.replace(
                              '[ANSWER]',
                              '',
                            );
                            return (
                              <HStack
                                className="col-cs analyticsRow"
                                style={{ margin: '8px auto',}}
                                key={j + '-' + i}
                              >
                                <Text
                                  as="h5"
                                  textStyle="h5"
                                  style={{ padding: '10px' }}
                                >
                                  {message}
                                </Text>
                              </HStack>
                            );
                          },
                        )}
                        {(Object.keys(askedQuestion.threads?.posts).length ||
                          0) === 0 && (
                          <Text className="body-x-me">
                            {t("chat.noAnswersFound")}
                          </Text>
                        )}
                      </StyledTable>
                    ) : null}
                  </Module>
                );
              })}
            </div>
          ) : (
            tabActiveIndex === 1 ? (
            <div className="ask-question-container">
              <Text><b>{t("chat.note")}</b> {t("chat.allQuestionsAreAnsweredByRealPerson")}</Text>
              <br />
              <textarea
                className="ask-question-textarea"
                onChange={(e: any) => {
                  setStudentQuestion(e.target.value);
                }}
                placeholder={t("chat.askAQuestion")}
                value={studentQuestion}
              />
              <div className="ask-question-file-section">
                <CloudinaryUploadWidget
                  isImageOnly={true}
                  onImageUpload={(newImageInfo) => setImageInfo(newImageInfo)}
                  onDelete={() => {
                    setImageInfo(null);
                  }}
                />
              </div>
              <div className="ask-question-buttons">
                {isAskingQuestion ? (
                  <Loader />
                ) : (
                  <>
                    <button
                      className="ask-question-button"
                      onClick={() => {
                        if (!studentQuestion.trim()) {
                          toast({
                            title: t("chat.emptyQuestionError"),
                            status: "error",
                          })
                          return;
                        }
                        askQuestion(studentQuestion);
                      }}
                    >
                      {t("chat.askAQuestion")}
                    </button>
                    <button
                      type="button"
                      className="ask-question-button cancel"
                      onClick={() => {
                        setShowAskQuestionContainer(!showAskQuestionContainer);
                      }}
                    >
                      {t("general.cancel")}
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <SendFeedback />
          ))}
        </Drawer>
      )}

      {initialShowChat &&
        (isRoleHidingChat ? (
          <ChatButton
            id="question-btn"
            data-hover={t("chat.askAQuestion")}
            aria-label={t("chat.askAQuestion")}
            onClick={() => {
              localStorage.setItem(
                OLD_ANSWER_COUNT_KEY,
                answerCount.toString(),
              );
              setShowAskQuestionContainer(!showAskQuestionContainer);
            }}
          >
            {answerCount > 0 && (
              <UnreadAnswers
                style={{
                  background: isOpenAnswerCount ? '#605e5e' : 'red',
                  left: '-5px',
                  top: '-5px',
                }}
              >
                {answerCount > 9 ? '9+' : answerCount}
              </UnreadAnswers>
            )}
            <QuestionIcon boxSize="24px" />
          </ChatButton>
        ) : (
          <ChatButton id="chat-btn" data-hover={t("chat.teamChat")} aria-label={t("chat.teamChat")} onClick={openChat}>
            {unreadChannels > 0 && (
              <UnreadChannels>
                {unreadChannels > 9 ? '9+' : unreadChannels}
              </UnreadChannels>
            )}
            <ChatIcon boxSize="24px" />
          </ChatButton>
        ))}
    </>
  );
};

export default Chat;
